import {
  EXISTING_FAMILY,
  ExistingFamilyConflict,
  emptyReferralChild,
  emptyReferralGuardian,
  isBlankChild,
  isBlankGuardian,
  REFERRAL_LIMITS,
  type EditReferralPayloadInput,
  type ReferralChildInput,
  type ReferralCycleOption,
  type ReferralEditState,
  type ReferralDto,
  type ReferralPayloadInput,
  type RecommendationAllotment,
} from '@hv/shared';
import { ApiError } from '../api/client';

export type LocalChild = ReferralChildInput & { uid: string };
export type LocalPayload = Omit<ReferralPayloadInput, 'children'> & { children: LocalChild[] };

export interface DraftForm {
  cycleId: string | null;
  currentStep: number;
  countsTowardAllotment: boolean;
  payload: LocalPayload;
}

let uidCounter = 0;
export const newUid = () => `child-${++uidCounter}`;

export const newChild = (): LocalChild => ({ ...emptyReferralChild(), uid: newUid() });

const padGuardians = (guardians: ReferralPayloadInput['guardians']) => {
  const out = guardians.slice(0, REFERRAL_LIMITS.guardians);
  while (out.length < REFERRAL_LIMITS.guardians) out.push(emptyReferralGuardian());
  return out;
};

export function newDraftForm(cycles: readonly ReferralCycleOption[]): DraftForm {
  return {
    cycleId: cycles.length === 1 ? cycles[0]!.id : null,
    currentStep: 1,
    countsTowardAllotment: false,
    payload: {
      familyLastName: '',
      guardians: padGuardians([]),
      relationship: '',
      children: [newChild()],
      connectionNarrative: '',
      communityContribution: '',
      firstChoice: 'unknown',
      enrollmentInterest: '',
      additionalInformation: '',
    },
  };
}

export function formFromReferral(r: ReferralDto): DraftForm {
  const children = r.payload.children.map((c) => ({ ...c, uid: newUid() }));
  return {
    cycleId: r.cycle?.id ?? null,
    currentStep: r.currentStep,
    countsTowardAllotment: r.countsTowardAllotment,
    payload: {
      ...r.payload,
      guardians: padGuardians(r.payload.guardians),
      children: children.length ? children : [newChild()],
    },
  };
}

export interface DraftRequest {
  cycleId: string | null;
  currentStep: number;
  countsTowardAllotment: boolean;
  payload: ReferralPayloadInput;
  sentUids: string[];
}

export function toRequest(form: DraftForm): DraftRequest {
  const sent = form.payload.children.filter((c) => !isBlankChild(c));
  return {
    cycleId: form.cycleId,
    currentStep: form.currentStep,
    countsTowardAllotment: form.countsTowardAllotment,
    payload: {
      ...form.payload,
      guardians: form.payload.guardians.filter((g) => !isBlankGuardian(g)),
      children: sent.map(({ uid: _uid, ...c }) => c),
    },
    sentUids: sent.map((c) => c.uid),
  };
}

export function mergeSaved(
  form: DraftForm,
  sentUids: readonly string[],
  saved: ReferralDto,
): DraftForm {
  const keys = new Map(sentUids.map((uid, i) => [uid, saved.payload.children[i]?.childKey]));
  return {
    ...form,
    payload: {
      ...form.payload,
      children: form.payload.children.map((c) => {
        const childKey = keys.get(c.uid);
        return childKey && childKey !== c.childKey ? { ...c, childKey } : c;
      }),
    },
  };
}

export function pruneBlankChildren(form: DraftForm): DraftForm {
  const kept = form.payload.children.filter((c) => !isBlankChild(c));
  if (kept.length === form.payload.children.length) return form;
  return {
    ...form,
    payload: { ...form.payload, children: kept.length ? kept : [form.payload.children[0]!] },
  };
}

export interface AllotmentState {
  limit: number;
  usedByOthers: number;
  full: boolean;
}

export function allotmentFor(
  allotment: RecommendationAllotment,
  cycleId: string | null,
  referralId: string | null,
): AllotmentState {
  const usage = allotment.cycles.find((c) => c.cycle.id === cycleId);
  const usedByOthers = usage?.activeReferralIds.filter((id) => id !== referralId).length ?? 0;
  return { limit: allotment.limit, usedByOthers, full: usedByOthers >= allotment.limit };
}

export function existingFamilyFrom(err: unknown): ExistingFamilyConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409 || err.code !== EXISTING_FAMILY) return null;
  const parsed = ExistingFamilyConflict.safeParse(err.body);
  return parsed.success ? parsed.data : { existingReferralId: null, existingReferenceNumber: null };
}

export function editStateOf(form: DraftForm): ReferralEditState {
  return { payload: form.payload, countsTowardAllotment: form.countsTowardAllotment };
}

export function toEditPayload(form: DraftForm): EditReferralPayloadInput {
  const { familyLastName: _family, guardians: _guardians, ...answers } = toRequest(form).payload;
  return answers;
}
